import Box from '@mui/material/Box'
import List from '@mui/material/List'
import Popover from '@mui/material/Popover'
import React, { useMemo } from 'react'

import HHSectionPlaceholder from 'components/common/HHSectionPlaceholder'
import NoteListItem from 'components/notes/common/NoteListItem'
import NoteSkeleton from 'components/notes/common/NoteSkeleton'
import T from 'T'
import { Z_INDEX } from 'theme/zIndex'

import useCombinedNotes from './useCombinedNotes'

const HHSectionPlaceholderUntyped = HHSectionPlaceholder as React.ElementType
const NoteListItemUntyped = NoteListItem as React.ElementType
const NoteSkeletonUntyped = NoteSkeleton as React.ElementType

const popoverSx = { pointerEvents: 'none', zIndex: Z_INDEX.BADGE_POPOVER }
const anchorOrigin = { vertical: 'bottom', horizontal: 'right' } as const
const transformOrigin = { vertical: 'top', horizontal: 'right' } as const
// Reachable paper under a click-through root: the truck icon inside carries its own tooltip.
const paperSx = { width: 'max-content', pointerEvents: 'auto' as const }

type CombinedNotesPopoverProps = {
  anchorEl: HTMLElement | null
  workOrderId?: string
  locationId?: string
  configuredServiceId?: string
  onClose: () => void
  onPaperMouseEnter?: () => void
  onPaperMouseLeave?: () => void
  showNoteVisibilityOnMobileIcon?: boolean
}

const CombinedNotesPopover = ({
  anchorEl,
  workOrderId,
  locationId,
  configuredServiceId,
  onClose,
  onPaperMouseEnter,
  onPaperMouseLeave,
  showNoteVisibilityOnMobileIcon = false,
}: CombinedNotesPopoverProps) => {
  const isOpen = Boolean(anchorEl)
  const { mergedNotes, isFetching } = useCombinedNotes({ configuredServiceId, enabled: isOpen, locationId, workOrderId })
  // Head of the canonical order — pinned first, then newest — so it is the row the dialog lists first.
  const topNote = mergedNotes[0]
  const slotProps = useMemo(
    () => ({ paper: { sx: paperSx, onMouseEnter: onPaperMouseEnter, onMouseLeave: onPaperMouseLeave } }),
    [onPaperMouseEnter, onPaperMouseLeave]
  )

  return (
    <Popover
      disableRestoreFocus
      anchorEl={anchorEl}
      anchorOrigin={anchorOrigin}
      open={isOpen}
      slotProps={slotProps}
      sx={popoverSx}
      transformOrigin={transformOrigin}
      onClose={onClose}
    >
      {isFetching && (
        <Box minWidth={300}>
          <NoteSkeletonUntyped rows={1} showActions={false} />
        </Box>
      )}
      {!isFetching && !topNote && <HHSectionPlaceholderUntyped title={T.NO_NOTES_YET} />}
      {!isFetching && topNote && (
        <List sx={{ py: 0 }}>
          <NoteListItemUntyped
            note={topNote}
            showDivider={false}
            showNoteVisibilityOnMobileIcon={showNoteVisibilityOnMobileIcon}
            showSecondaryActions={false}
            type={topNote.noteTypeTag}
          />
        </List>
      )}
    </Popover>
  )
}

export default CombinedNotesPopover
